/**
 * Light/dark mode for the console. The MECHANISM only -- no colour, no token, no value.
 *
 * Every value the two modes differ by belongs to Nexus 2.0 and arrives vendored via
 * `npm run sync:design-templates`. This module's whole job is to decide which of the design
 * system's two modes is in force and to say so on `<html>`, where the tokens are scoped. If you
 * came here looking for a hex, it is in `src/vendor/nexus-design-system/<ref>/tokens.root.css`
 * and it is not editable from this repo.
 *
 * ---------------------------------------------------------------------------------------------
 * THREE STATES, TWO OUTCOMES, AND WHY THAT ASYMMETRY IS THE DESIGN.
 *
 * The stored PREFERENCE has three values: 'system', 'light', 'dark'. What lands on `<html>` has
 * two: `data-theme="light"` or `data-theme="dark"`, always one of them, never absent. 'system' is
 * resolved here -- read from `prefers-color-scheme` and re-read when it changes -- rather than
 * expressed in CSS.
 *
 * That is a deliberate trade and worth defending, because the obvious alternative is to leave the
 * attribute off for 'system' and let a media query decide. Doing that means every dark rule has
 * to be written twice:
 *
 *     :root[data-theme='dark']                         { ...40 tokens... }
 *     @media (prefers-color-scheme: dark) {
 *       :root:not([data-theme='light'])                { ...the same 40 tokens... }
 *     }
 *
 * Two copies, kept in step by hand, in a file that is GENERATED from the design system. The
 * generator would have to emit both and every future consumer would inherit the duplication. So
 * the media query still decides the outcome -- it is read below, and listened to -- it just does
 * not decide it twice in two languages.
 *
 * The cost of that choice is real and is paid in `index.html`: the attribute has to be stamped
 * before first paint or the page flashes the wrong mode. See the inline script there, and
 * `STORAGE_KEY` below, which is the one thing the two copies of this logic must agree on.
 * `tests/unit/theme.test.ts` asserts they do.
 */

/** What the operator chose. Persisted. */
export type ThemePreference = 'system' | 'light' | 'dark'

/** What is actually on screen. Always one of two; 'system' has been resolved away. */
export type ResolvedTheme = 'light' | 'dark'

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark']

/**
 * The localStorage key, and the single point of contact with the pre-paint script in
 * `index.html`. That script cannot import this module -- it runs in `<head>`, before the bundle
 * -- so the string is duplicated there by necessity. It is exported rather than inlined so the
 * duplication is checkable, and a unit test reads `index.html` and asserts the two match. A
 * silent divergence here would not throw: the pre-paint script would read nothing, stamp the
 * system default, and the page would flash to the stored preference once React mounted. A flash
 * is exactly the failure the script exists to prevent, so it has to fail loudly in CI instead.
 */
export const STORAGE_KEY = 'nexus-fleet-console.theme'

/** The attribute the design system's tokens are scoped to, on `<html>`. */
export const THEME_ATTRIBUTE = 'data-theme'

const MEDIA_QUERY = '(prefers-color-scheme: dark)'

function isPreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark'
}

/**
 * Every storage access in this module is wrapped. `localStorage` throws rather than returning
 * null in a handful of real configurations -- Safari private browsing, a browser set to block
 * site data, an iframe with a restrictive sandbox -- and a theme preference is not worth taking
 * the console down for. An unreadable store degrades to 'system', which is the right answer
 * anyway when nothing is known about the operator's choice.
 */
function readStored(): ThemePreference | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return isPreference(raw) ? raw : null
  } catch {
    return null
  }
}

function writeStored(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    // Non-fatal by design: the theme still applies for this session, it just will not survive a
    // reload. Better than a console that cannot render because a preference could not be saved.
  }
}

/** The stored preference, or 'system' when nothing has been chosen or storage is unreadable. */
export function readPreference(): ThemePreference {
  return readStored() ?? 'system'
}

/** What the operating system currently prefers. */
function systemTheme(): ResolvedTheme {
  // `matchMedia` is absent in some test environments and in very old browsers. Light is the
  // safer default of the two: the console's untokenised surfaces are light.
  if (typeof window.matchMedia !== 'function') return 'light'
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light'
}

/** Collapse a three-state preference to the two-state reality. */
export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference
}

/**
 * Put the resolved mode on `<html>`, where Nexus 2.0's tokens are scoped.
 *
 * Returns what it resolved to, so a caller does not have to resolve twice to know what it just
 * did -- and so a caller cannot resolve a SECOND time and get a different answer, which is
 * possible across an OS theme change and would leave state disagreeing with the DOM.
 */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference)
  document.documentElement.setAttribute(THEME_ATTRIBUTE, resolved)
  return resolved
}

/** Persist a choice and apply it in one step. */
export function setPreference(preference: ThemePreference): ResolvedTheme {
  writeStored(preference)
  return applyTheme(preference)
}

/**
 * Notify when the OS preference changes. The listener fires regardless of the stored preference
 * and the caller decides whether it is relevant, because the caller is the one that knows
 * whether it is currently following the system.
 *
 * Registered unconditionally rather than only while the preference is 'system'. Subscribing and
 * unsubscribing as the preference changes is more moving parts for no gain, and it has a real
 * bug in it: switch to 'light', change the OS theme, switch back to 'system', and a listener
 * that was torn down never saw the change, so the console follows a stale system value until the
 * next OS event.
 */
export function subscribeToSystemTheme(onChange: (theme: ResolvedTheme) => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {}
  const query = window.matchMedia(MEDIA_QUERY)
  const handler = (event: MediaQueryListEvent) => onChange(event.matches ? 'dark' : 'light')
  query.addEventListener('change', handler)
  return () => query.removeEventListener('change', handler)
}

/**
 * The next preference in the cycle, for a single-button control: system → light → dark → system.
 *
 * System leads because it is the default, so the first press of the button is always a move AWAY
 * from following the OS and towards an explicit choice -- which is what somebody reaching for the
 * control wanted. Cycling light → dark → system would make the first press a no-op for anybody
 * already on a light OS.
 */
export function nextPreference(current: ThemePreference): ThemePreference {
  const index = THEME_PREFERENCES.indexOf(current)
  return THEME_PREFERENCES[(index + 1) % THEME_PREFERENCES.length]
}

/** How each state names itself in the UI. Kept here so the control and its tests agree. */
export const THEME_LABELS: Record<ThemePreference, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
}

/** Lucide icon names, resolved dynamically by `components/ui/Icon.tsx`. */
export const THEME_ICONS: Record<ThemePreference, string> = {
  system: 'monitor',
  light: 'sun',
  dark: 'moon',
}
